import { isAxiosError } from "axios";
import { runWithErrorReporting } from "$root/api/error-reporting";
import { useAsync } from "@mdotm/utils/promise";
import { downloadContentDisposition } from "$root/utils/files";
import { flattenProperties } from "$root/api/repository";
import type { StackRouterProps } from "$root/components/common/Stack";
import { StackSidePanelContext } from "$root/components/common/Stack/StackSidePanel";
import { IconWalls } from "$root/components/IconWall";
import { useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { ReactQueriesWrapperBase } from "$root/components/ReactQueryWrapper";
import { platformToast, ToastContent } from "$root/notification-system/toast";
import { AgGridWithModules } from "$root/third-party-integrations/ag-grid/ag-grid";
import { generateIntentId, trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { useMappedQueries, useQueryNoRefetch, useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { queryClient } from "$root/third-party-integrations/react-query/typedQueryClient";
import {
	columnChooserActionLoadingContextKey,
	settingsColdDef,
} from "$root/ui-lib/table/custom-column/setting/base-cell";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { withPermissionTooltip } from "$root/ui-lib/table/extra/permission-tooltip";
import { minutes, seconds, ToastableError } from "$root/utils";
import { Card } from "$root/widgets-architecture/layout/Card";
import {
	type DataResponseDTO,
	type ParameterizedConceptDTO,
	ViewModeEnum,
	DistributionBinningModeEnum,
	type ConceptDefinitionDTO,
} from "@eu.mdotm.api/smart-workspaces";
import {
	chartTypeDrawsLegend,
	chartSeriesConcepts,
	supportedChartTypes,
	workspaceChartTypeLabels,
} from "../chart-type";
import type { OptionModelDto } from "@eu.mdotm.api/sphere";
import { Icon, Text } from "@mdotm/shadcn/legacy/components";
import { useDebounced, useDeepEqualMemo } from "@mdotm/shadcn/legacy/react-extensions";
import { atom, atomWithCompare, readAtom, useAtom, useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import {
	instanceEqualsOrFastDeepEqual,
	maybeCall,
	type SetterOrUpdaterParam,
	typedObjectEntries,
	unpromisify,
} from "@mdotm/shadcn/legacy/utils";
import { Pagination } from "@mdotm/shadcn/mdotm/components";
import { useMandatoryContext, useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { defer, noop, Result, unreachable } from "@mdotm/utils/runtime";
import { useQueries } from "@tanstack/react-query";
import type {
	CellValueChangedEvent,
	ColDef,
	ColumnState,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItems,
	GetRowIdFunc,
	MenuItemDef,
} from "ag-grid-community";
import type { AgGridReact } from "ag-grid-react";
import Immutable, { type Map, OrderedMap, OrderedSet } from "immutable";
import { type Ref, useCallback, useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import z from "zod";
import { aclByArea, roleByArea, roleFromAcl } from "../../acl/checkers/all";
import { validateACLPermissions } from "../../acl/checkers/shared";
import Disclaimer from "../../acl/Disclaimer";
import { spawnShareDialogSmart, updateAclWithFeedbacks } from "../../acl/spawn-share-dialog";
import { portfolioEntityManagementActions } from "../../portfolio/entity-management";
import { portfolioRepository } from "../../portfolio/repository";
import { user$ } from "../../user";
import { formFromConcept } from "../concept-form";
import { pageSizeOptions, type ParameterizedConceptDTOWithMandatoryUuid } from "../ctx";
import {
	type WorkspaceAction,
	buildDataRequest,
	selectConcepts,
	selectDataPredicate,
	selectNameConceptUuid,
	selectPagination,
	selectRequestConcepts,
	selectSort,
	useWorkspaceStore,
} from "../state";
import { createFilterFromParametrizedConcept } from "../filter-utils";
import { workspaceHandlers } from "../hooks";
import { workspaceRepository } from "../repository";
import { isCustomAttributeConcept } from "../services/sharedConceptService";
import { extractDataProviderValue } from "../WorkspaceDataDefinitionCast";
import {
	cellValueToCustomAttributeDto,
	type ColSettings,
	conceptColDef,
	type Row,
	type TagOptionsMap,
} from "../data-type";
import { WorkspacePortfolioBulkActions } from "../WorkspacePortfolioBulkActions";
import {
	WorkspaceCreateTableSettingStack,
	WorkspaceEditTableSettingsStack,
	WorkspaceTableSettingStack,
} from "./WorkspaceTableSettings";

/** @internal — exported for tests */
export function isEmptyValue(value: unknown): boolean {
	if (value == null) return true;
	if (typeof value === "string" && value.trim() === "") return true;
	if (Array.isArray(value) && value.length === 0) return true;
	return false;
}

function WorkspaceTable() {
	const { dispatch, slices, readState } = useWorkspaceStore();
	const workspaceUuid = useAtomValue(slices.document$)?.uuid;
	const sidePanelCtx = useMandatoryContext(StackSidePanelContext);
	const columns = useAtomValue(slices.columns$);
	const allParametrizedConcepts = useAtomValue(slices.concepts$);
	// single source of truth for the grid element; the handlers receive it rather than
	// each owning their own state (which previously fanned out re-renders via multiRef)
	const [gridEl, setGridEl] = useState<AgGridReact<Row> | null>(null);
	const selectionGridHandler = workspaceHandlers().useSelectionGrid<Row>(gridEl);
	const sortGridHandler = workspaceHandlers().useSortGrid<Row>(gridEl);

	const parametrizedConceptsMap = OrderedMap(allParametrizedConcepts.map((c) => [c.uuid, c]));

	const pagination = useAtomValue(slices.pagination$);
	const requestState = useAtomValue(slices.requestState$);

	const workspaceConceptsMap = workspaceRepository.getConceptMapDefinitions.useQuery();

	const workspace = useAtomValue(slices.dto$);
	const projection = workspace.grid?.projection;

	// The three parts of the request that also go into the query key on their own. Still memoized, though the
	// memos are no longer load-bearing: the key is hashed by value and `useMappedQueries` compares its deps
	// deeply, so what they buy now is a stable identity rather than a stable render.
	const nameParametrizedConceptUuid = selectNameConceptUuid(requestState);
	const predicate = useDeepEqualMemo(() => selectDataPredicate(requestState), [requestState]);
	const filteredParameterizedConcepts = useDeepEqualMemo(
		() => selectRequestConcepts(requestState, projection),
		[requestState, projection],
	);

	const workspaceDataQueryKey = [
		...workspaceRepository.getWorkspaceData.fullQueryKey,
		"grid",
		...flattenProperties(filteredParameterizedConcepts),
		...flattenProperties(sortGridHandler.sort),
		...flattenProperties(predicate),
		pagination.page,
		pagination.size,
		nameParametrizedConceptUuid,
	];
	const workspaceDataQuery = useQueryNoRefetch(
		workspaceDataQueryKey,
		workspaceRepository.getWorkspaceData.getQueryConfig(
			buildDataRequest(requestState, {
				projection,
				extras: {
					limit: pagination.size,
					offset: (pagination.page - 1) * pagination.size,
					sorting: sortGridHandler.sort ? [sortGridHandler.sort] : undefined,
				},
			}),
		).queryFn,
		{
			keepPreviousData: true,
			cacheTime: minutes(5),
			staleTime: minutes(0),
			enabled: Boolean(columns?.length) && workspace.viewMode === "TABLE",
		},
	);

	const rowsDataQuery = useMappedQueries(
		([dataResponse, conceptMap]) => {
			const parametrizedConceptMap = Object.fromEntries(
				filteredParameterizedConcepts.map((concept) => [concept.uuid, concept]),
			);
			return dataResponseToRows(dataResponse, parametrizedConceptMap, conceptMap);
		},
		[filteredParameterizedConcepts],
		[workspaceDataQuery, workspaceConceptsMap],
	);

	/**
	 * Every portfolio on the page the grid is showing — what "Select all in page" selects.
	 *
	 * Read off the row data rather than asked of the grid: `api.getRenderedNodes()` answers with the
	 * *virtualised* slice that currently has DOM, so with a page size larger than the viewport it named a
	 * subset, and the subset moved with the scroll position. Pagination here is the server's and no grid-side
	 * filter narrows `rows`, so a page of rows is exactly a page.
	 */
	const uuidsInPage = useMemo(() => (rowsDataQuery.data ?? []).map((row) => row.uuid), [rowsDataQuery.data]);

	useQueryReactor(workspaceDataQuery, {
		onSuccess(data) {
			void dispatch({ type: "setResultTotal", total: data.pagination?.total ?? 0 });
		},
		onError() {
			setGridEl(null);
		},
	});

	const { updateQueue } = useWorkspaceStore();

	const refs = useUnsafeUpdatedRef({
		parametrizedConceptsMap,
		workspaceConceptsMap,
		updateQueue,
		workspaceDataQueryKey,
		projection,
	});

	const [handleExportWorkspaceGrid, exportState] = useAsync(async () => {
		// The same request the grid is showing, rebuilt from the state as it is at the click rather than from
		// the render that installed the handler — which is what the ref this used to carry six fields in was
		// emulating. The projection stays on the ref: it is the *document's*, not the store's.
		const state = readState();
		const { page, size } = selectPagination(state);
		const sort = selectSort(state);
		const config = buildDataRequest(state, {
			projection: refs.current.projection,
			extras: { limit: size, offset: (page - 1) * size, sorting: sort ? [sort] : undefined },
		});
		await runWithErrorReporting(
			async () => {
				try {
					gridEl?.api?.refreshHeader();
					const file = await workspaceRepository.exportWorkspaceDataRaw.fetchQuery(config, workspaceUuid);
					await downloadContentDisposition(file);
				} finally {
					gridEl?.api?.refreshHeader();
				}
			},
			{
				area: "workspace",
				attemptedOperation: {
					message: "export workspace data",
					payload: JSON.stringify(config),
				},
			},
		).catch(noop);
	});
	const isExporting = exportState.kind === "loading";

	const [_, setStatusCellMap] = useAtom(statusCellMap$);

	const onCellValueChanged = useCallback(
		(event: CellValueChangedEvent<Row>) => {
			const {
				parametrizedConceptsMap,
				workspaceConceptsMap: { data: conceptsMap },
				updateQueue,
				workspaceDataQueryKey,
			} = refs.current;
			const cellKey = getCellKey(event);
			setStatusCellMap((latest) => latest.set(cellKey, "DEFAULT"));

			const isObjectValue = typeof event.oldValue === "object" || typeof event.newValue === "object";
			if (
				isObjectValue
					? instanceEqualsOrFastDeepEqual(event.oldValue, event.newValue)
					: event.oldValue === event.newValue
			) {
				return;
			}

			const parsed = Result.ok(event)
				.andThen((e) =>
					Result.fromBool(
						conceptsMap != null,
						() => ({ e, conceptsMap: conceptsMap! }),
						() => "cannot edit this cell, missing conceptMap",
					),
				)
				.andThen(({ e, conceptsMap }) => {
					const investmentUuid = e.data?.uuid;
					const conceptUuid = e.colDef.colId;
					return Result.fromBool(
						investmentUuid != null && conceptUuid != null,
						() => ({ e, conceptsMap, investmentUuid: investmentUuid, conceptUuid: conceptUuid! }),
						() => "cannot edit this cell, missing investment uuid or concept uuid",
					);
				})
				.andThen(({ e, conceptsMap, investmentUuid, conceptUuid }) => {
					const parametrizedConcept = parametrizedConceptsMap.get(conceptUuid);
					return Result.fromBool(
						parametrizedConcept?.conceptId != null,
						() => ({ e, conceptsMap, investmentUuid, conceptUuid, parametrizedConcept: parametrizedConcept! }),
						() => "cannot edit this cell, missing concept id",
					);
				})
				.andThen(({ e, conceptsMap, investmentUuid, conceptUuid, parametrizedConcept }) => {
					const concept = conceptsMap.get(parametrizedConcept.conceptId);
					return Result.fromNullable(
						concept?.dataType,
						(dataType) => ({
							e,
							conceptsMap,
							investmentUuid,
							conceptUuid,
							parametrizedConcept,
							concept: concept!,
							dataType,
						}),
						() =>
							`cannot edit this cell, cannot find concept dataType with conceptId: ${parametrizedConcept.conceptId}`,
					);
				})
				.andThen(({ e, conceptsMap, investmentUuid, conceptUuid, parametrizedConcept, concept, dataType }) =>
					Result.fromBool(
						isCustomAttributeConcept(dataType),
						() => ({ e, conceptsMap, investmentUuid, conceptUuid, parametrizedConcept, concept }),
						() => "cannot edit this cell",
					),
				);

			if (parsed.isErr()) {
				setStatusCellMap((latest) => latest.set(cellKey, "ERROR"));
				throw new Error(parsed.unwrapErr());
			}

			const { investmentUuid, conceptUuid, parametrizedConcept, concept } = parsed.unwrap();
			const isEmpty = isEmptyValue(event.newValue);
			const customAttributeUUid = concept.id?.substring("customAttributeId: ".length - 1).trim() ?? "";
			const dto = isEmpty
				? null
				: cellValueToCustomAttributeDto(customAttributeUUid, concept.dataType!, event.newValue);

			queryClient.setQueryData(workspaceDataQueryKey, (prev: typeof workspaceDataQuery.data) => ({
				...prev,
				items: prev?.items?.map((item) =>
					item.predefinedFields?.uuid !== investmentUuid
						? item
						: {
								...item,
								dictionary: {
									...item.dictionary,
									[parametrizedConcept.uuid]: {
										...item.dictionary?.[parametrizedConcept.uuid],
										value: {
											...item.dictionary?.[parametrizedConcept.uuid]?.value,
											value: isEmpty
												? undefined
												: ((dto?.selectedTags ?? dto?.dateValue ?? dto?.numberValue ?? dto?.text) as unknown as object),
										},
									},
								},
							},
				),
			}));

			return updateQueue.enqueue(() =>
				runWithErrorReporting(
					async () => {
						try {
							const customAttributeUUid = concept.id?.substring("customAttributeId: ".length - 1).trim();
							if (!customAttributeUUid) {
								throw new Error("");
							}

							if (!dto || isEmpty) {
								return portfolioRepository.removeCustomAttributes(investmentUuid, [
									{ attributeUuid: customAttributeUUid },
								]);
							}

							await portfolioRepository.updateCustomAttributes(investmentUuid, [dto]);
							setStatusCellMap((latest) => latest.set(cellKey, "SUCCESS"));
							event.api.refreshCells({ rowNodes: [event.node] });
						} catch (error) {
							setStatusCellMap((latest) => latest.set(cellKey, "ERROR"));
							event.api.refreshCells({ rowNodes: [event.node] });
							// A 400 says why the value was refused (e.g. a SmartX model id outside the customer's enterprise).
							const reason =
								isAxiosError<{ message?: unknown }>(error) && error.response?.status === 400
									? error.response.data?.message
									: undefined;
							throw typeof reason === "string" && reason ? new ToastableError(reason, { cause: error }) : error;
						}
					},
					{
						area: "global",
						attemptedOperation: { message: `edit workspace cell ${conceptUuid} for ${investmentUuid}` },
					},
				),
			);
		},
		[refs, setStatusCellMap],
	);

	if (!columns?.length) {
		return (
			<Card className="border border-solid border-(--palette-N100)" data-qualifier="WorkspaceTable">
				<IconWalls.WorkspaceMissingConcepts
					onClick={() =>
						sidePanelCtx.push([
							WorkspaceTableSettingStack,
							() => <WorkspaceCreateTableSettingStack title="New column" />,
						])
					}
					buttonContent={
						<>
							<Icon icon="Outline1" />
							Add column
						</>
					}
				/>
			</Card>
		);
	}

	return (
		<>
			<Card className="border border-solid border-(--palette-N100)">
				<ReactQueriesWrapperBase queries={[rowsDataQuery, workspaceConceptsMap]}>
					{([rows, conceptsMap]) => (
						<>
							<WorkspaceTableInner
								conceptsMap={conceptsMap}
								parametrizedConceptsMap={parametrizedConceptsMap}
								rows={rows}
								innerRef={setGridEl}
								workspaceUuid={workspaceUuid}
								onCellValueChanged={onCellValueChanged}
								onExportWorkspaceGrid={handleExportWorkspaceGrid}
								isExporting={isExporting}
							/>
							<Pagination
								className="pt-2"
								totalResults={pagination.total}
								currentPage={pagination.page}
								onPageChange={(page) => void dispatch({ type: "setPage", page })}
								pageSizeOptions={pageSizeOptions}
								// The PUT and the analytics event used to be enqueued by hand here, with a comment
								// apologising for bypassing the observer. They are the middleware's now.
								onPageSizeChange={(size) => void dispatch({ type: "setPageSize", size })}
								pageSize={pagination.size}
							/>
						</>
					)}
				</ReactQueriesWrapperBase>
			</Card>
			{gridEl && (
				<WorkspacePortfolioBulkActions
					uuidsInPage={uuidsInPage}
					selection={selectionGridHandler.selection}
					onSelect={(newSelection, selectionType) =>
						selectionGridHandler.setSelection(newSelection, {
							skipGriSelectionMatch: selectionType === "all",
						})
					}
				/>
			)}
		</>
	);
}

/** @internal — exported for tests */
export function dataResponseToRows(
	dataResponse: DataResponseDTO,
	parameterizedConceptMap: Record<string, ParameterizedConceptDTO>,
	conceptMap: Immutable.Map<string, ConceptDefinitionDTO>,
): Array<Row> {
	return (
		dataResponse.items?.flatMap((x) => {
			const uuid = x.predefinedFields?.uuid;
			if (uuid) {
				return [
					{
						...Object.fromEntries(
							typedObjectEntries(x.dictionary ?? {}).map(([conceptUuid, concept]) => {
								const conceptId = parameterizedConceptMap[conceptUuid]?.conceptId;
								if (!conceptId) {
									return [conceptUuid, { value: concept.value }];
								}

								const conceptDefinition = conceptMap.get(conceptId);
								if (!conceptDefinition?.dataType) {
									return [conceptUuid, { value: concept.value }];
								}

								return [conceptUuid, extractDataProviderValue(concept, conceptDefinition.dataType)];
							}),
						),
						...(x.predefinedFields ?? {}),
						uuid,
					} as Row,
				];
			}
			return [];
		}) ?? []
	);
}

/** @internal — exported for tests */
export const statusCellMap$ = atomWithCompare<Immutable.Map<string, "ERROR" | "SUCCESS" | "DEFAULT">>(Immutable.Map());
function WorkspaceTableInner({
	conceptsMap,
	rows,
	onCellValueChanged,
	parametrizedConceptsMap,
	innerRef,
	workspaceUuid,
	onExportWorkspaceGrid,
	isExporting,
}: {
	rows: Array<Row>;
	onCellValueChanged: (event: CellValueChangedEvent<Row, any, any>) => Promise<void> | void;
	/** attributes that define the columns */
	parametrizedConceptsMap: Map<string, ParameterizedConceptDTOWithMandatoryUuid>;
	/** enrich columns definitions */
	conceptsMap: Immutable.Map<string, ConceptDefinitionDTO>;
	innerRef?: Ref<AgGridReact<Row>>;
	workspaceUuid?: string;
	onExportWorkspaceGrid: () => void;
	isExporting: boolean;
}) {
	const { dispatch, readState, slices } = useWorkspaceStore();
	const columnsSettings = useAtomValue(slices.columns$);

	/**
	 * The settled column layout, on its way to the PUT.
	 *
	 * The grid reports its layout as a stream — `stateUpdated` fires per frame of a resize drag — so the
	 * samples reach the store as `fromGrid` and persist nothing (see the note on `reorderColumns`). This is
	 * the trailing edge of that stream, dispatched once, as the same action the settings panel dispatches.
	 * Without it a resize or a header drag lived in the store and nowhere else, and the column snapped back on
	 * the next load.
	 *
	 * It reads the layout back out of the store rather than closing over the sample that scheduled it: the
	 * samples are already in there, and anything else that moved a column inside the debounce window
	 * (the settings panel) has persisted itself — so replaying an older value here would undo it.
	 *
	 * `unmountBehavior: "flush"` is the other half. Leaving the page is the one moment where the last edit has
	 * no next edit to subsume it. One second is the interval `usePersistColState` already uses for the same job
	 * on the tables that persist to local storage.
	 */
	const persistGridColumns = useDebounced(
		() => void dispatch({ type: "reorderColumns", columns: readAtom(slices.columns$) }),
		{ debounceInterval: seconds(1), unmountBehavior: "flush" },
	);

	const sidePanelCtx = useMandatoryContext(StackSidePanelContext);
	const refs = useUnsafeUpdatedRef({
		sidePanelCtx,
		dispatch,
		onExportWorkspaceGrid,
		persistGridColumns,
	});

	const customAttributeTagConceptIds = useDeepEqualMemo(() => {
		let ids = OrderedSet<string>();
		for (const colSettings of columnsSettings ?? []) {
			if (!colSettings.colId) {
				continue;
			}
			// `conceptReferencesResolve` is enforced, so a persisted column names a concept the pool holds.
			const parametrizedConcept = parametrizedConceptsMap.get(colSettings.colId) ?? unreachable();
			const concept = conceptsMap.get(parametrizedConcept.conceptId);
			if (concept?.dataType === "CUSTOM_ATTRIBUTE_TAG" && concept.id) {
				ids = ids.add(concept.id);
			}
		}
		return ids.toArray();
	}, [columnsSettings, parametrizedConceptsMap, conceptsMap]);

	const tagOptionsQueries = useQueries({
		queries: customAttributeTagConceptIds.map((conceptId) =>
			workspaceRepository.getConceptCustomAttributeTagOptions.getQueryConfig(conceptId),
		),
	});

	const tagOptionsMap = useDeepEqualMemo(() => {
		const map: Record<string, { options: Array<Required<OptionModelDto>>; isLoading: boolean }> = {};
		customAttributeTagConceptIds.forEach((conceptId, i) => {
			const query = tagOptionsQueries[i];
			map[conceptId] = {
				options: query?.data ?? [],
				isLoading: query?.isLoading ?? false,
			};
		});
		return map;
	}, [customAttributeTagConceptIds, tagOptionsQueries]);

	const columns = useDeepEqualMemo(() => {
		const settingDef = settingsColdDef();
		return [
			...workspaceConceptToColumns({
				conceptsMap,
				parametrizedConceptsMap,
				columnsSettings,
				workspaceUuid,
				tagOptionsMap,
				dispatch: (action) => void refs.current.dispatch(action),
			}),
			{
				suppressHeaderFilterButton: true,
				sortable: false,
				headerName: "Action",
				colId: "ACTION",
				headerComponent: settingDef.columnMenuChooserWithLoading,
				cellRenderer: settingDef.contextMenuTrigger,
				mainMenuItems() {
					return [
						{
							name: "Add column",
							icon: renderToStaticMarkup(<Icon icon="Outline1" size={16} />),
							action() {
								refs.current.sidePanelCtx.push([
									WorkspaceTableSettingStack,
									() => <WorkspaceCreateTableSettingStack title="Add column" />,
								]);
							},
						},
						{
							name: "Choose and order columns",
							icon: '<span class="ag-icon ag-icon-columns" role="presentation" unselectable="on"></span>',
							action() {
								refs.current.sidePanelCtx.push(WorkspaceTableSettingStack);
							},
						},
						"separator",
						{
							name: "Export",
							icon: renderToStaticMarkup(<Icon icon="Dowload" size={16} />),
							action() {
								refs.current.onExportWorkspaceGrid();
							},
						},
					];
				},
				width: 48,
				editable: false,
				pinned: "right",
			} satisfies ColDef<any>,
		];
	}, [conceptsMap, parametrizedConceptsMap, columnsSettings, workspaceUuid, tagOptionsMap]);

	/**
	 * The grid's own layout, back into the workspace.
	 *
	 * `agExtra.useSyncColStateWithAtom` reads *and* writes, so this is the one place the area hands out a
	 * writable atom — and what it writes is a `dispatch`, not a slice. `fromGrid` is what keeps a resize from
	 * enqueueing a PUT and a MixPanel event per frame; {@link persistGridColumns} is what makes sure the
	 * layout the user settled on is nonetheless saved. See the note on the action.
	 */
	const [gridColumns$] = useState(() =>
		atom(
			(get) => get(slices.columns$),
			(get, _set, next: SetterOrUpdaterParam<ColumnState[] | null>) => {
				const value = maybeCall(next, get(slices.columns$));
				// Only the columns the workspace owns: the checkbox and ACTION columns are the grid's, and the
				// pool filter is the boundary with a stateful third party — the grid can still be reporting a
				// column whose concept this workspace has just deleted, and a reference that resolves to nothing
				// is now a thrown invariant rather than an empty header.
				const pool = new Set(selectConcepts(readState()).map((concept) => concept.uuid));
				const smartWorkspaceColumn = value?.filter(
					(column) => Result.fromThrower(() => z.string().uuid().parse(column.colId)).isOk() && pool.has(column.colId),
				);
				if (smartWorkspaceColumn) {
					for (const col of smartWorkspaceColumn) {
						// sort/sortIndex live in the sort slice (applied to the grid by useSortGrid), never in the
						// persisted column layout — otherwise an extra sort arrow re-appears on reload.
						delete col.sort;
						delete col.sortIndex;
						for (const k in col) {
							// BE treats nulls as undefined
							// TODO: tell BE not to remove nulls
							if (col[k as keyof typeof col] === null) {
								delete col[k as keyof typeof col];
							}
						}
					}
				}
				if (!instanceEqualsOrFastDeepEqual(readAtom(slices.columns$), smartWorkspaceColumn)) {
					void dispatch({ type: "reorderColumns", columns: smartWorkspaceColumn ?? null, fromGrid: true });
					// ...and once the grid stops reporting, the layout again as a settled one, which is what persists
					// it. Inside the guard on purpose: a grid that reports the layout the store already holds has
					// nothing to save, so opening the page cannot enqueue a PUT of its own.
					refs.current.persistGridColumns.invoke();
				}
			},
		),
	);

	const columnsPersistence = agExtra.useSyncColStateWithAtom(gridColumns$);

	const { push } = useTypedNavigation();
	const getContextMenuItems = useCallback<GetContextMenuItems<Row, any>>(
		(row) => {
			const portfolio = row.node?.data;
			if (!portfolio) {
				return ["copy"];
			}

			const canEnhance = portfolio.status === "READY" || portfolio.status === "ACCEPTED";
			const user = readAtom(user$);
			const entityManagementActions = portfolioEntityManagementActions(user, portfolio, {
				onDelete() {
					workspaceRepository.invalidate(workspaceRepository.getWorkspaceData.fullQueryKey);
				},
				onDuplicate() {
					workspaceRepository.invalidate(workspaceRepository.getWorkspaceData.fullQueryKey);
				},
				onRename() {
					workspaceRepository.invalidate(workspaceRepository.getWorkspaceData.fullQueryKey);
				},
			});

			return [
				...(aclByArea.portfolio.canCreateProposal(user.id, portfolio.richAcl?.acl ?? [])
					? [
							{
								icon: canEnhance
									? renderToStaticMarkup(<Icon icon="Enhance" size={16} />)
									: renderToStaticMarkup(<Icon icon="Enhance-Not-available" size={16} />),
								disabled: !canEnhance,
								action: () => {
									const intentId = generateIntentId();
									trackMixPanelEvent("portfolio.entityActions.createProposalIntent", {
										intentId,
										portfolio: {
											uuid: portfolio.uuid,
											name: portfolio.name,
										},
									});
									if (workspaceUuid) {
										push("Portfolios/EditPortfolioWithWorkspace", {
											workspaceUid: workspaceUuid,
											portfolioUid: portfolio.uuid,
											intentId,
										});
									} else {
										push("Portfolios/EditPortfolio", { portfolioUid: portfolio.uuid, intentId });
									}
								},
								menuItemParams: {
									"data-qualifier": "PortfolioStudio/PortfolioList/DropdownMenu/DropdownItem(Enhance)",
								},
								name: "Create proposal",
							},
						]
					: []),
				{
					icon: renderToStaticMarkup(<Icon icon="Delete" size={16} />),
					disabled: !entityManagementActions.deleteAsync,
					action: unpromisify(entityManagementActions.deleteAsync ?? noop),
					menuItemParams: {
						"data-qualifier": "PortfolioStudio/PortfolioList/DropdownMenu/DropdownItem(Delete)",
					},
					name: "Delete",
				},
				{
					icon: renderToStaticMarkup(<Icon icon="Content-Copy" size={16} />),
					disabled: !entityManagementActions.duplicateAsync,
					action: unpromisify(entityManagementActions.duplicateAsync ?? noop),
					menuItemParams: {
						"data-qualifier": "PortfolioStudio/PortfolioList/DropdownMenu/DropdownItem(Duplicate)",
					},
					name: "Duplicate",
				},
				{
					icon: renderToStaticMarkup(<Icon icon="Edit" size={16} />),
					action: unpromisify(entityManagementActions.renameAsync ?? noop),
					menuItemParams: {
						"data-qualifier": "PortfolioStudio/PortfolioList/DropdownMenu/DropdownItem(Rename)",
					},
					name: "Rename",
				},

				{
					icon: renderToStaticMarkup(<Icon icon="share" size={16} />),
					action: unpromisify(async () => {
						const user = readAtom(user$);
						const { name, uuid } = portfolio;
						await spawnShareDialogSmart({
							userId: user.id,
							async saveAclHandler({ aclByEntity }) {
								await updateAclWithFeedbacks(async () => {
									await portfolioRepository.updateACL(uuid, aclByEntity[uuid]);
									workspaceRepository.invalidate(workspaceRepository.getWorkspaceData.fullQueryKey);
								});
							},
							header: `Share "${name ?? "-"}"`,
							disclaimerProvider: (entities) => <Disclaimer entity="INVESTMENT" entities={entities} />,
							customerAndLinkedAclProvider: () => portfolioRepository.getACL.fetchQuery(uuid),
							entityMetadata: { uuid: uuid, name: name ?? "-", entityType: "INVESTMENT" },
							area: "portfolio",
						});
					}),
					menuItemParams: {
						"data-qualifier": "PortfolioStudio/PortfolioList/DropdownMenu/DropdownItem(Share)",
					},
					name: "Share",
					disabled: !validateACLPermissions(user.id, portfolio.richAcl?.acl ?? [], roleByArea.portfolio.EDITOR),
				},
			];
		},
		[push, workspaceUuid],
	);

	const agCtx = useMemo(
		() => ({
			sidePanelCtx,
			compareQueryConfig: portfolioRepository.comparisonDetails.getQueryConfig,
			[columnChooserActionLoadingContextKey]: isExporting,
		}),
		[sidePanelCtx, isExporting],
	);

	return (
		<AgGridWithModules<Row>
			{...agExtra.checkboxColumn({
				mode: "multiRow",
				selectAll: "all",
			})}
			onGridReady={columnsPersistence.onGridReady}
			onStateUpdated={columnsPersistence.onStateUpdated}
			innerRef={innerRef}
			context={agCtx}
			columnDefs={columns}
			rowData={rows}
			getRowId={getRow}
			getContextMenuItems={getContextMenuItems}
			onCellValueChanged={onCellValueChanged}
			maxConcurrentDatasourceRequests={1}
			stopEditingWhenCellsLoseFocus
			undoRedoCellEditing
			undoRedoCellEditingLimit={20}
		/>
	);
}
const getRow: GetRowIdFunc<Row, any> = (row) => row.data.uuid;

type WorkspaceConceptsToColumnsParams = {
	parametrizedConceptsMap: Immutable.Map<string, ParameterizedConceptDTOWithMandatoryUuid>;
	conceptsMap: Immutable.Map<string, ConceptDefinitionDTO>;
	columnsSettings: ColumnState[] | null;
	workspaceUuid?: string;
	tagOptionsMap: TagOptionsMap;
	/**
	 * The quick actions in a column's header menu. One function instead of two controllers, and read through
	 * a ref because a colDef outlives the render that built it.
	 */
	dispatch(action: WorkspaceAction): void;
};

/** @internal — exported for tests */
export function workspaceConceptToColumns(params: WorkspaceConceptsToColumnsParams) {
	return (params.columnsSettings ?? []).flatMap((colSettings) => {
		// `conceptReferencesResolve` is enforced, so the column resolves. The *definition* behind it is a
		// different question and still a branch: a column added through `addColumn {property}` carries
		// `pendingCustomAttributeConceptId` until the API answers, and the catalogue has no entry for it, so
		// there is nothing to build a colDef from yet.
		const parametrizedConcept = params.parametrizedConceptsMap.get(colSettings.colId) ?? unreachable();
		const concept = params.conceptsMap.get(parametrizedConcept.conceptId);
		if (!concept?.dataType) {
			return [];
		}

		return [
			conceptToColumn({
				colSettings,
				parametrizedConcept,
				concept,
				workspaceUuid: params.workspaceUuid,
				tagOptionsMap: params.tagOptionsMap,
				dispatch: params.dispatch,
			}),
		];
	});
}

function conceptToColumn({
	colSettings,
	parametrizedConcept,
	concept,
	workspaceUuid,
	tagOptionsMap,
	dispatch,
}: {
	colSettings: ColSettings;
	parametrizedConcept: ParameterizedConceptDTOWithMandatoryUuid;
	concept: ConceptDefinitionDTO;
	workspaceUuid?: string;
	tagOptionsMap: TagOptionsMap;
	dispatch(action: WorkspaceAction): void;
}) {
	// rows will be replaced, disable client side sorting
	const comparator = () => 0;

	// A kind the metric supports but that yields no series has nothing to draw — the form the kind reads
	// declares no source — so it is not offered rather than offered and broken. Asking the builder is also
	// what keeps this list honest for the line chart, whose sources are its history's and not the column's.
	const chartableTypes = supportedChartTypes(concept).filter(
		(chartType) => chartSeriesConcepts(concept, chartType, parametrizedConcept).length > 0,
	);

	const mainMenuItems: (DefaultMenuItem | MenuItemDef<any, any>)[] = [
		"sortAscending",
		"sortDescending",
		{
			name: "Clear all sorting",
			icon: renderToStaticMarkup(<Icon icon="Reload" size={16} />),
			action(e) {
				e.api.applyColumnState({ defaultState: { sort: null } });
			},
		},
		"separator",
		"pinSubMenu",
		{
			name: "Autosize",
			subMenu: ["autoSizeThis", "autoSizeAll"],
		},
		"separator",
		{
			name: "Table settings",
			icon: renderToStaticMarkup(<Icon icon="Table" size={16} />),
			action(e) {
				(e.context.sidePanelCtx as StackRouterProps).push(WorkspaceTableSettingStack);
			},
		},
		{
			name: "Edit column",
			icon: renderToStaticMarkup(<Icon icon="Edit" size={16} />),
			action(e) {
				runWithErrorReporting(
					async () => {
						const availableTagsConcepts = await workspaceRepository.getTagConceptDefinitions.fetchQuery();

						(e.context.sidePanelCtx as StackRouterProps).push([
							WorkspaceTableSettingStack,
							() => (
								<WorkspaceEditTableSettingsStack
									id={parametrizedConcept.uuid}
									title={parametrizedConcept.label}
									initialData={formFromConcept(parametrizedConcept, concept, {
										tagConcepts: availableTagsConcepts,
									})}
								/>
							),
						]);
					},
					{
						area: "portfolio",
						attemptedOperation: {
							message: "edit column",
							payload: JSON.stringify({
								parametrizedConcept,
								concept,
							}),
						},
					},
				).catch(noop);
			},
		},
		"separator",
		// The quick action has no panel to ask in, so it offers one entry per chart type the metric
		// supports and creates that type directly. An empty list means the metric charts as nothing.
		//
		// A column's chain pins a level rather than expanding one, so the stacked column never appears
		// here — it would have to invent a dimension the user never chose. Adding the chart from the panel
		// and picking "All" on a level is the way to it.
		...(chartableTypes.length === 0
			? []
			: [
					{
						name: "Create Chart",
						icon: renderToStaticMarkup(<Icon icon="Chart" size={16} />),
						subMenu: chartableTypes.map((chartType) => ({
							name: workspaceChartTypeLabels[chartType],
							action() {
								const parameterizedConcepts = chartSeriesConcepts(concept, chartType, parametrizedConcept);
								dispatch({
									type: "addChart",
									concepts: parameterizedConcepts,
									chart: {
										chartLegend: {
											// Only the types whose legend names something the plot cannot — the stacked column's
											// categories, the line chart's portfolios — see `chartTypeDrawsLegend`.
											visible: chartTypeDrawsLegend(chartType),
										},
										chartType,
										distributionProperties:
											chartType === "FREQUENCY" ? { mode: DistributionBinningModeEnum.Auto } : undefined,
										label: parametrizedConcept.label ?? "",
										description: "",
										manualThreshold: undefined,
										relativeThreshold: undefined,
									},
								});
								platformToast({
									children: (
										<ToastContent
											onClick={() => {
												dispatch({ type: "setView", view: ViewModeEnum.Chart });
												defer(() => {
													window.scrollTo(0, Number.MAX_SAFE_INTEGER);
												});
											}}
										>
											<Text type="Body/M/Bold">New chart created</Text>
											<br />
											<Text type="Body/M/Book">Click to view</Text>
										</ToastContent>
									),
									icon: "Icon-full-ok",
									severity: "success",
								});
							},
						})),
					},
				]),
		{
			name: "Create Filter",
			icon: renderToStaticMarkup(<Icon icon="Filter-no" size={16} />),
			action(e) {
				runWithErrorReporting(
					() =>
						createFilterFromParametrizedConcept(
							e.context.sidePanelCtx as StackRouterProps,
							concept,
							parametrizedConcept,
						),
					{
						area: "portfolio",
						attemptedOperation: {
							message: "unable to create filter from parametrized concept",
							payload: JSON.stringify({
								concept,
								parametrizedConcept,
							}),
						},
					},
				).catch(noop);
			},
		},
	];

	const dataType = concept.dataType!;

	const baseOptions = {
		headerName: parametrizedConcept.label,
		colId: parametrizedConcept.uuid,
		editable(row) {
			return editableHelper(row, concept);
		},
		minWidth: 150,
		mainMenuItems,
		comparator,
		cellClassRules: {
			"!bg-(--global-palette-warning-100)": (params) => {
				if (!params.data) {
					return false;
				}
				const cellKey = getCellKey({ data: params.data, colDef: params.colDef });
				const statusMap = readAtom(statusCellMap$);
				return statusMap.get(cellKey) === "ERROR";
			},
		},
	} satisfies ColDef<Row>;

	// sort/sortIndex are owned by sort$ and applied to the grid imperatively (see useSortGrid); keeping
	// them out of the initial colDef prevents a stale sort arrow from re-appearing on load.
	let layoutColSettings = colSettings;
	if (colSettings) {
		const { sort: _sort, sortIndex: _sortIndex, ...rest } = colSettings;
		layoutColSettings = rest;
	}

	const col = conceptColDef(dataType, {
		concept,
		parametrizedConcept,
		options: baseOptions,
		colSettings: layoutColSettings,
		tagOptionsMap,
		workspaceUuid,
	});

	if (concept.editable) {
		col.tooltipComponent = withPermissionTooltip<Row>(baseOptions.editable, col.tooltipComponent);
	}

	return col;
}

function editableHelper<T extends Row>(row: EditableCallbackParams<T>, concept: ConceptDefinitionDTO): boolean {
	const user = readAtom(user$);
	return roleFromAcl("portfolio", user.id, row.data?.richAcl?.acl ?? []) !== "VIEWER" && (concept.editable ?? false);
}

function getCellKey(event: { colDef: ColDef<Row, any>; data: Row }) {
	const colId = event.colDef.colId;
	const investmentUuid = event.data.uuid;
	const cellKey = `${colId}:${investmentUuid}`;
	return cellKey;
}

export default WorkspaceTable;
